// Shaved ice, per vertex of the finished mound's rest skin. Turning the
// handle shaves the ice block onto the mound, which grows until the block is
// used up. Pressing the fluffy ice packs it down. Syrup poured on the top soaks in where it lands and runs down the
// mound: each point holds only so much, and the rest flows to lower
// neighbours. Soaked ice takes the syrup's colour, mixing where syrups meet,
// and settles a little as it melts down.
export class ShavedIce{
  constructor({rest,floor,top,rate=.16,capacity=.4,start=0}){
    this.rest=rest;this.floor=floor;this.top=top;this.rate=rate;this.capacity=capacity;this.start=start;
    const n=rest.length/3;this.soak=new Float32Array(n);this.colour=new Float32Array(n*3).fill(1);this.wet=new Float32Array(n);
    // Each point's lower neighbours, for the syrup to run into.
    let span=0;for(let i=0;i<n;i++)span=Math.max(span,Math.hypot(rest[i*3],rest[i*3+2]));
    const reach=span*.11,cell=reach,grid=new Map(),key=(x,y,z)=>`${x},${y},${z}`;
    for(let i=0;i<n;i++){const k=key(Math.floor(rest[i*3]/cell),Math.floor(rest[i*3+1]/cell),Math.floor(rest[i*3+2]/cell));if(!grid.has(k))grid.set(k,[]);grid.get(k).push(i);}
    this.below=[];
    for(let i=0;i<n;i++){
      const x=rest[i*3],y=rest[i*3+1],z=rest[i*3+2],list=[];
      for(let a=-1;a<=1;a++)for(let b=-1;b<=1;b++)for(let c=-1;c<=1;c++)for(const j of grid.get(key(Math.floor(x/cell)+a,Math.floor(y/cell)+b,Math.floor(z/cell)+c))??[]){
        const d=Math.hypot(rest[j*3]-x,rest[j*3+1]-y,rest[j*3+2]-z);
        if(j!==i&&d<reach&&rest[j*3+1]<y-1e-6)list.push([j,(y-rest[j*3+1])/d]);
      }
      const sum=list.reduce((s,[,w])=>s+w,0);this.below.push(list.map(([j,w])=>[j,sum?w/sum:0]));
    }
    this.reset();
  }
  reset(){this.volume=this.start;this.block=1;this.soak.fill(0);this.wet.fill(0);this.colour.fill(1);this.pours=[];}
  // Pressing fluffy ice packs it down, though never below about half its height.
  pack(amount){const before=this.volume;this.volume=Math.max(.4,this.volume-amount);return before-this.volume;}
  // Pour a syrup of colour [r,g,b] (0..1) on the top for a moment.
  pour(colour){this.pours.push({colour:[...colour],left:1.4});}
  // How far point i has settled, as a share of its height.
  slump(i){return .16*Math.min(1,this.soak[i]);}
  step(dt,crank){
    const events=[];
    // Shavings top the mound up until the cup is heaped again.
    if(crank>0&&this.block>0&&this.volume<1){
      const shaved=Math.min(this.block,1-this.volume,dt*this.rate*crank);this.block-=shaved;this.volume+=shaved;events.push('shave');
      if(this.volume>=1-1e-9){this.volume=1;events.push('full');}
    }
    const n=this.soak.length,R=this.rest;
    // New syrup lands on the highest skin of the mound as it stands now.
    for(const p of this.pours){
      const amount=Math.min(p.left,dt)*3.2;p.left-=dt;if(amount<=0)continue;
      const level=this.floor+(this.top-this.floor)*this.height;
      for(let i=0;i<n;i++){
        const h=(R[i*3+1]-this.floor)/(this.top-this.floor);if(h<.82)continue;
        this.add(i,amount*(h-.82)/.18,p.colour);
      }
      if(level<0)break;
    }
    this.pours=this.pours.filter(p=>p.left>0);
    // Free syrup runs down: what a point cannot hold flows to lower neighbours.
    const flow=new Float32Array(n),flowColour=new Float32Array(n*3);
    for(let i=0;i<n;i++){
      const extra=this.wet[i];if(extra<=1e-6)continue;
      const moving=Math.min(extra,extra*dt*7);this.wet[i]-=moving;
      for(const [j,w] of this.below[i]){flow[j]+=moving*w;for(let a=0;a<3;a++)flowColour[j*3+a]+=moving*w*this.colour[i*3+a];}
    }
    for(let i=0;i<n;i++)if(flow[i]>0)this.add(i,flow[i],[flowColour[i*3]/flow[i],flowColour[i*3+1]/flow[i],flowColour[i*3+2]/flow[i]]);
    return events;
  }
  // Syrup arriving at point i: soak in up to capacity, keep the rest free to run.
  add(i,amount,colour){
    const room=Math.max(0,this.capacity-this.soak[i]),taken=Math.min(room,amount*.4),before=this.soak[i];
    this.soak[i]+=taken;this.wet[i]+=amount-taken;
    // Syrup running through tints the ice too, so colours mix where they meet.
    const w=before<=1e-6?1:Math.min(1,(taken+amount*.25)/(before+taken+amount*.25));
    for(let a=0;a<3;a++)this.colour[i*3+a]=this.colour[i*3+a]*(1-w)+colour[a]*w;
  }
  // The mound's height as a share of full: it grows with the shaved volume.
  get height(){return this.volume**.7;}
}
